import { Button } from "@/components/ui/button";
import { useTheme } from "../../theme/ThemeContext";

export const BRAND = {
  ivory: "#faf7f1",
  paper: "#f5efe4",
  ink: "#1a1713",
  ink2: "#3a332a",
  muted: "#6b6258",
  hairline: "#e8dfce",
  gold: "#bf9a2f",
  goldDeep: "#8a6f1f",
  terracotta: "#b85c3d",
  sage: "#6b7a4a",
};

// Dark counterparts for the editorial palette. Mirrors BRAND so callers can
// pick the right pair at render time.
export const BRAND_DARK = {
  ivory: "#15120e",
  paper: "#1d1915",
  ink: "#faf7f1",
  ink2: "#c8bfa8",
  muted: "#9a9288",
  hairline: "#2a2520",
  gold: "#d4ad3f",
  goldDeep: "#bf9a2f",
  terracotta: "#d47358",
  sage: "#8c9a6a",
};

export function useEditorialPalette() {
  const { appearance } = useTheme();
  const isDark = appearance === "dark";
  return {
    isDark,
    ivory: isDark ? BRAND_DARK.ivory : BRAND.ivory,
    paper: isDark ? BRAND_DARK.paper : BRAND.paper,
    ink: isDark ? BRAND_DARK.ink : BRAND.ink,
    ink2: isDark ? BRAND_DARK.ink2 : BRAND.ink2,
    muted: isDark ? BRAND_DARK.muted : BRAND.muted,
    hairline: isDark ? BRAND_DARK.hairline : BRAND.hairline,
    gold: BRAND.gold,
    goldDeep: BRAND.goldDeep,
    terracotta: BRAND.terracotta,
    sage: BRAND.sage,
    // Dark "band" sections (the Promise band, ink-variant cards) stay dark in
    // both themes — the page should feel uniformly dark in dark mode, not
    // pierced by a contrasting light slab.
    bandBg: BRAND.ink,
    bandText: BRAND.ivory,
    bandMuted: "#c8bfa8",
  };
}

export const EDITORIAL_FONT = {
  serif: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
  sans: "'Work Sans', 'Inter', system-ui, sans-serif",
  arabic: "'Noto Naskh Arabic', serif",
};

export function Star8({ size = 14, color = BRAND.gold, stroke = 1.25, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={stroke}
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <rect x="5" y="5" width="14" height="14" />
      <rect x="5" y="5" width="14" height="14" transform="rotate(45 12 12)" />
    </svg>
  );
}

export function PatternTile({ opacity = 0.05, color = BRAND.gold }) {
  const patternId = `geo-${Math.random().toString(36).slice(2, 8)}`;
  return (
    <svg
      width="100%"
      height="100%"
      aria-hidden="true"
      style={{ position: "absolute", inset: 0, opacity, pointerEvents: "none" }}
    >
      <defs>
        <pattern id={patternId} x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
          <g stroke={color} strokeWidth="0.6" fill="none">
            <rect x="15" y="15" width="30" height="30" />
            <rect x="15" y="15" width="30" height="30" transform="rotate(45 30 30)" />
            <circle cx="30" cy="30" r="2" fill={color} stroke="none" />
            <circle cx="0" cy="0" r="1" fill={color} stroke="none" />
            <circle cx="60" cy="0" r="1" fill={color} stroke="none" />
            <circle cx="0" cy="60" r="1" fill={color} stroke="none" />
            <circle cx="60" cy="60" r="1" fill={color} stroke="none" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill={`url(#${patternId})`} />
    </svg>
  );
}

export function Eyebrow({ number, children, color = BRAND.gold, className = "" }) {
  return (
    <div
      className={`inline-flex items-center gap-2.5 text-[11px] sm:text-xs ${className}`}
      style={{
        fontFamily: EDITORIAL_FONT.sans,
        fontWeight: 600,
        letterSpacing: "0.18em",
        textTransform: "uppercase",
        color,
      }}
    >
      {number && (
        <>
          <span
            style={{
              fontFamily: EDITORIAL_FONT.serif,
              fontStyle: "italic",
              fontWeight: 400,
              textTransform: "none",
              letterSpacing: "-0.01em",
              fontSize: "14px",
            }}
          >
            {number}
          </span>
          <span style={{ width: 20, height: 1, background: color }} />
        </>
      )}
      <span>{children}</span>
    </div>
  );
}

export function EditorialShell({ children, className = "", style = {} }) {
  const palette = useEditorialPalette();
  return (
    <div
      className={`min-h-screen relative ${className}`}
      style={{
        background: palette.ivory,
        color: palette.ink,
        fontFamily: EDITORIAL_FONT.sans,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function EditorialCard({
  variant = "paper",
  children,
  className = "",
  style = {},
  ...rest
}) {
  const p = useEditorialPalette();
  const variants = {
    ivory: { background: p.ivory, color: p.ink, border: `1px solid ${p.hairline}` },
    paper: { background: p.paper, color: p.ink, border: `1px solid ${p.hairline}` },
    ink: { background: p.bandBg, color: p.bandText, border: `1px solid ${p.isDark ? p.hairline : "#2a2520"}` },
    gold: { background: BRAND.gold, color: BRAND.ink, border: "none" },
  };
  return (
    <div
      className={`rounded-2xl ${className}`}
      style={{ ...variants[variant], ...style }}
      {...rest}
    >
      {children}
    </div>
  );
}

export function SerifHeading({
  level = 2,
  children,
  italic = false,
  className = "",
  style = {},
  ...rest
}) {
  const Tag = `h${level}`;
  return (
    <Tag
      className={className}
      style={{
        fontFamily: EDITORIAL_FONT.serif,
        fontWeight: 420,
        letterSpacing: "-0.03em",
        lineHeight: 1,
        fontStyle: italic ? "italic" : "normal",
        textWrap: "balance",
        ...style,
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

function getPillVariants(p) {
  return {
    primary: {
      background: p.ink,
      color: p.ivory,
      border: `1px solid ${p.ink}`,
    },
    ghost: {
      background: "transparent",
      color: p.ink,
      border: `1px solid ${p.ink}33`,
    },
    gold: {
      background: BRAND.gold,
      color: "#fff",
      border: `1px solid ${BRAND.gold}`,
    },
    light: {
      background: p.ivory,
      color: p.ink,
      border: `1px solid ${p.ivory}`,
    },
    outline: {
      background: "transparent",
      color: p.ink,
      border: `1px solid ${p.ink}`,
    },
  };
}

export function PillButton({
  variant = "primary",
  children,
  className = "",
  style = {},
  asChild = false,
  size = "default",
  ...rest
}) {
  const palette = useEditorialPalette();
  const variants = getPillVariants(palette);
  const v = variants[variant] || variants.primary;
  const sizeStyle =
    size === "lg"
      ? { padding: "16px 26px", fontSize: 15 }
      : size === "sm"
      ? { padding: "10px 16px", fontSize: 13 }
      : { padding: "14px 22px", fontSize: 14 };
  return (
    <Button
      asChild={asChild}
      className={`rounded-full inline-flex items-center gap-2 font-medium transition-opacity hover:opacity-90 disabled:opacity-50 ${className}`}
      style={{
        ...v,
        ...sizeStyle,
        fontFamily: EDITORIAL_FONT.sans,
        fontWeight: 550,
        ...style,
      }}
      {...rest}
    >
      {children}
    </Button>
  );
}

export function Divider({ color = BRAND.hairline, style = {}, className = "" }) {
  return (
    <div
      className={className}
      style={{ height: 1, width: "100%", background: color, ...style }}
      aria-hidden="true"
    />
  );
}
